import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import DemoVideo from "@/components/DemoVideo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";
import { categoryTint } from "@/lib/categoryColors";
import { ArrowRight, FileText, Sparkles, Printer, ShieldCheck, Layers, Building2, Search, Scale } from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import { areaLabel, directoryText } from "@/lib/attorneyI18n";
import { NC_COUNTIES } from "@/lib/ncCounties";
import { getCountyPref } from "@/lib/countyPref";
import { useSosEnabled } from "@/lib/siteConfig";

const SITE = process.env.REACT_APP_BACKEND_URL || "";

export default function Landing() {
  const [stats, setStats] = useState({ total: 0, categories: [] });
  const [hideSos, setHideSos] = useState(false);
  const siteSosOn = useSosEnabled();
  const [attyQ, setAttyQ] = useState("");
  const [attyArea, setAttyArea] = useState("");
  const [attyCounty, setAttyCounty] = useState(getCountyPref()?.county || "");
  const [areas, setAreas] = useState([]);
  const navigate = useNavigate();
  const { t, lang, forms, formsTotal } = useI18n();
  const D = directoryText(lang);
  useEffect(() => { api.get("/forms/categories").then(({ data }) => setStats(data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/profile").then(({ data }) => setHideSos(!!data?.hide_sos)).catch(() => {}); }, []);
  useEffect(() => { api.get("/directory/practice-areas").then(({ data }) => setAreas(data.practice_areas || [])).catch(() => {}); }, []);
  useEffect(() => {
    const sync = () => setAttyCounty(getCountyPref()?.county || "");
    window.addEventListener("nc-county-pref-changed", sync);
    return () => window.removeEventListener("nc-county-pref-changed", sync);
  }, []);
  const sosTotal = (stats.categories || []).filter((c) => (c.name || "").startsWith("SOS ·")).reduce((a, c) => a + c.count, 0);
  const findAttorney = (e) => {
    e.preventDefault();
    const p = new URLSearchParams();
    if (attyQ.trim()) p.set("q", attyQ.trim());
    if (attyArea) p.set("area", attyArea);
    if (attyCounty) p.set("county", attyCounty);
    const qs = p.toString();
    navigate(`/find-attorney${qs ? `?${qs}` : ""}`);
  };

  return (
    <div>
      <Helmet>
        <title>NC Legal Forms — Fill, Print & File North Carolina Court Forms Online</title>
        <meta name="description" content={`Complete ${formsTotal ? formsTotal.toLocaleString("en-US") : forms} official North Carolina Judicial Branch (AOC) court forms online. AI-assisted filling, autofill, live preview, print and download court-ready PDFs for divorce, custody, child support, small claims and more — in all 100 NC counties.`} />
        <link rel="canonical" href={`${SITE}/`} />
        <meta property="og:title" content="NC Legal Forms — North Carolina Court Forms Online" />
        <meta property="og:description" content="Fill, edit, print and file official NC court forms online with AI assistance." />
        <meta property="og:type" content="website" />
      </Helmet>
      <Navbar />

      {/* Hero */}
      <section className="relative max-w-7xl mx-auto px-6 pt-16 pb-24 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7 animate-fade-up">
          <p className="overline text-accent mb-6">{t("hero.eyebrow")}</p>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[0.98] tracking-tighter mb-6">
            {t("hero.title1")}<br /><span className="italic text-accent">{t("hero.title2")}</span>
          </h1>
          <p className="text-lg text-muted-foreground max-w-xl mb-8 leading-relaxed">
            {t("hero.subtitle")} <span className="text-foreground font-medium">{t("hero.freeTrial")}</span>
          </p>
          <div className="flex flex-wrap gap-4">
            <Link to="/find"><Button size="lg" data-testid="hero-finder-btn" className="rounded-sm h-12 px-7 bg-accent text-accent-foreground hover:bg-primary hover:text-primary-foreground transition-colors gap-2"><Sparkles size={18} /> {t("hero.findAI")}</Button></Link>
            <Link to="/forms"><Button size="lg" variant="outline" data-testid="hero-browse-btn" className="rounded-sm h-12 px-7 hover:bg-secondary gap-2">{t("hero.browse")} <ArrowRight size={18} /></Button></Link>
          </div>
          <form onSubmit={findAttorney} data-testid="hero-attorney-search" className="mt-6 max-w-3xl">
            <div className="flex flex-col sm:flex-row items-stretch gap-2">
              <div className="flex items-center gap-2 border-2 border-black dark:border-white/30 bg-background pl-4 h-12 flex-1 focus-within:border-accent transition-colors">
                <Scale size={18} className="text-accent shrink-0" />
                <Input
                  value={attyQ}
                  onChange={(e) => setAttyQ(e.target.value)}
                  data-testid="hero-attorney-search-input"
                  placeholder={t("home.lawyerSearch")}
                  className="border-0 shadow-none focus-visible:ring-0 px-1 h-full bg-transparent"
                />
              </div>
              <select
                value={attyArea}
                onChange={(e) => setAttyArea(e.target.value)}
                data-testid="hero-attorney-area-select"
                className="h-12 border-2 border-black dark:border-white/30 bg-background px-3 text-sm focus:border-accent outline-none transition-colors sm:w-44"
              >
                <option value="">{t("home.allAreas")}</option>
                {areas.map((a) => <option key={a} value={a}>{areaLabel(lang, a)}</option>)}
              </select>
              <select
                value={attyCounty}
                onChange={(e) => setAttyCounty(e.target.value)}
                data-testid="hero-attorney-county-select"
                className="h-12 border-2 border-black dark:border-white/30 bg-background px-3 text-sm focus:border-accent outline-none transition-colors sm:w-40"
              >
                <option value="">{D.allCounties}</option>
                {NC_COUNTIES.map((c) => <option key={c.slug} value={c.name}>{D.county(c.name)}</option>)}
              </select>
              <Button type="submit" data-testid="hero-attorney-search-btn" className="rounded-none h-12 px-5 gap-2 bg-primary text-primary-foreground hover:bg-accent hover:text-accent-foreground transition-colors shrink-0">
                <Search size={16} /> {t("home.find")}
              </Button>
            </div>
          </form>
          <div className="flex flex-wrap gap-x-10 gap-y-4 mt-12">
            <div><p className="font-serif font-bold text-foreground text-4xl">{forms}</p><p className="text-xs font-bold text-foreground uppercase tracking-wider mt-1">{t("hero.statForms")}</p></div>
            <div><p className="font-serif font-bold text-foreground text-4xl">{stats.categories?.length || "15"}</p><p className="text-xs font-bold text-foreground uppercase tracking-wider mt-1">{t("hero.statCategories")}</p></div>
            <div><p className="font-serif font-bold text-foreground text-4xl">100%</p><p className="text-xs font-bold text-foreground uppercase tracking-wider mt-1">{t("hero.statPrint")}</p></div>
          </div>
        </div>
        <div className="lg:col-span-5 relative animate-fade-up" style={{ animationDelay: "0.15s" }}>
          <DemoVideo />
          <div className="absolute -bottom-6 -left-6 bg-primary text-primary-foreground p-6 w-56 hidden sm:block">
            <ShieldCheck className="text-accent mb-2" />
            <p className="text-sm leading-snug">{t("landing.badge")}</p>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="bg-secondary border-y border-border">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <p className="overline text-accent mb-3">{t("landing.catEyebrow")}</p>
          <h2 className="font-serif font-bold text-4xl tracking-tight mb-10 max-w-2xl">{t("landing.catTitle")}</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {(stats.categories?.length ? stats.categories : [{ name: "Child Support", count: 11 }, { name: "No-Contact Orders", count: 9 }]).map((c) => (
              <Link key={c.name} to={`/forms?category=${encodeURIComponent(c.name)}`} data-testid={`landing-cat-${c.name}`}
                style={{ backgroundColor: categoryTint(c.name) }}
                className="border-[3px] border-black p-8 flex items-center justify-between group text-black transition-transform hover:-translate-y-1">
                <div>
                  <Layers className="mb-4" />
                  <h3 className="font-serif font-bold text-2xl">{c.name}</h3>
                  <p className="text-sm text-black/70 mt-1">{t("landing.formsAvailable", { count: c.count })}</p>
                </div>
                <ArrowRight className="group-hover:translate-x-1 transition-transform" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Secretary of State forms */}
      <section className="max-w-7xl mx-auto px-6 pt-20">
        {!hideSos && siteSosOn && (
        <Link to="/sos-forms" data-testid="landing-sos-tile"
          className="group block bg-primary text-primary-foreground border-[3px] border-black p-10 lg:p-12 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8 transition-transform hover:-translate-y-1">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-accent mb-4">
              <span className="px-2 py-0.5 border border-accent/60">Free · Fillable</span>
              <span className="text-primary-foreground/70" data-testid="sos-tile-count">{sosTotal || 245}+ forms</span>
            </span>
            <div className="flex items-start gap-4">
              <span className="grid place-items-center w-12 h-12 bg-accent text-accent-foreground shrink-0"><Building2 size={24} /></span>
              <div>
                <h2 className="font-serif font-bold text-3xl lg:text-4xl tracking-tight leading-tight">NC Secretary of State forms</h2>
                <p className="text-primary-foreground/80 mt-2 leading-relaxed">
                  Business registration, charities, notary, trademarks, UCC and more — {sosTotal || 245}+ official NC Secretary of State forms, free to fill and print.
                </p>
              </div>
            </div>
          </div>
          <span className="inline-flex items-center gap-2 px-6 h-12 bg-accent text-accent-foreground font-medium shrink-0 group-hover:bg-white group-hover:text-primary transition-colors">
            Browse SOS forms <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
          </span>
        </Link>
        )}
      </section>

      {/* Features */}
      <section className="max-w-7xl mx-auto px-6 py-24">
        <div className="grid lg:grid-cols-3 gap-8">
          {[
            { icon: Sparkles, t: t("landing.feat1t"), d: t("landing.feat1d") },
            { icon: FileText, t: t("landing.feat2t"), d: t("landing.feat2d") },
            { icon: Printer, t: t("landing.feat3t"), d: t("landing.feat3d") },
          ].map((f, i) => (
            <div key={i} className="grid-card p-8 animate-fade-up" style={{ animationDelay: `${i * 0.1}s` }}>
              <span className="grid place-items-center w-11 h-11 bg-primary text-primary-foreground mb-5"><f.icon size={20} /></span>
              <h3 className="font-serif font-bold text-2xl mb-2 text-foreground">{f.t}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{f.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 pb-24">
        <div className="bg-primary text-primary-foreground p-12 lg:p-16 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
          <div>
            <p className="overline text-accent mb-3">{t("cta.eyebrow")}</p>
            <h2 className="font-serif text-4xl lg:text-5xl tracking-tight max-w-xl">{t("cta.title")}</h2>
          </div>
          <Link to="/pricing"><Button size="lg" data-testid="cta-bottom-btn" className="rounded-sm h-12 px-8 bg-accent text-accent-foreground hover:bg-white hover:text-primary transition-colors gap-2">{t("cta.seePlans")} <ArrowRight size={18} /></Button></Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
